import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, MapPin, Ruler, BedDouble, Gauge, ArrowRight, Eye } from 'lucide-react';
import { CompareButton } from './BoatTools';
import { STATUS_LABELS, STATUS_STYLE, PROGRAM_LABELS, DOC_LEVEL_LABELS, fmtPrice, fmtDate, boatTitle, isNew, imgUrl } from '../../lib/boats';

const BoatCard = ({ boat, isFav, onFav }) => {
  const photo = boat.photos?.[0];
  const title = boatTitle(boat);
  const sold = boat.status === 'sold';
  return (
    <article className="group bg-[#302f2c] border border-[#3f4816] rounded-2xl overflow-hidden flex flex-col hover:border-[#d9fb06]/60 hover:-translate-y-1 transition-all duration-300 shadow-lg" data-testid={`boat-card-${boat.slug}`}>
      <Link to={`/bateaux-a-vendre/${boat.slug}`} className="relative block aspect-[4/3] bg-[#1a1c1b] overflow-hidden">
        {photo ? (
          <img src={imgUrl(photo.thumb_url || photo.url)} alt={photo.alt || title} loading="lazy" width={photo.width} height={photo.height}
            className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${sold ? 'grayscale-[40%]' : ''}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#888680] text-sm">Photos à venir</div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          {isNew(boat) && !sold && <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-[#1a1c1b]/90 text-[#d9fb06] border border-[#d9fb06]/50">Nouveauté</span>}
          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${STATUS_STYLE[boat.status]}`}>{STATUS_LABELS[boat.status]}</span>
        </div>
        {boat.seen_by_vb && <span className="absolute bottom-3 left-3 flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#1a1c1b]/85 text-[#dfddd6]"><Eye size={12} className="text-[#d9fb06]" /> Vu par ValidatedBoat</span>}
      </Link>
      <div className="p-5 flex flex-col flex-1 gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-[#dfddd6] leading-tight truncate">{title}</h3>
            <p className="text-sm text-[#888680]">{[boat.year, boat.location].filter(Boolean).join(' · ')}</p>
          </div>
          {onFav && (
            <button type="button" onClick={() => onFav(boat.id)} aria-label={isFav ? 'Retirer des favoris' : 'Ajouter aux favoris'} aria-pressed={isFav}
              data-testid={`boat-fav-${boat.slug}`}
              className={`p-2 rounded-full border transition-colors ${isFav ? 'bg-[#d9fb06] border-[#d9fb06] text-[#1a1c1b]' : 'border-[#3f4816] text-[#888680] hover:text-[#d9fb06] hover:border-[#d9fb06]'}`}>
              <Heart size={16} fill={isFav ? 'currentColor' : 'none'} />
            </button>
          )}
        </div>
        {boat.price != null && !sold && <p className="text-2xl font-black text-[#d9fb06]">{fmtPrice(boat.price)}</p>}
        {sold && <p className="text-sm font-bold text-[#888680] uppercase">Vendu{boat.sold_at ? ` · ${fmtDate(boat.sold_at)}` : ''}</p>}
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#dfddd6]">
          {boat.length != null && <li className="flex items-center gap-1"><Ruler size={14} className="text-[#888680]" />{boat.length} m</li>}
          {(boat.cabins != null || boat.berths != null) && <li className="flex items-center gap-1"><BedDouble size={14} className="text-[#888680]" />{boat.cabins != null ? `${boat.cabins} cab.` : ''}{boat.cabins != null && boat.berths != null ? ' / ' : ''}{boat.berths != null ? `${boat.berths} couch.` : ''}</li>}
          {boat.engine_type && <li className="flex items-center gap-1"><Gauge size={14} className="text-[#888680]" />{boat.engine_type}{boat.engine_hours != null ? ` · ${boat.engine_hours} h` : ''}</li>}
          {boat.port && !boat.location && <li className="flex items-center gap-1"><MapPin size={14} className="text-[#888680]" />{boat.port}</li>}
        </ul>
        {boat.highlights?.length > 0 && (
          <ul className="text-sm text-[#dfddd6]/90 space-y-1">
            {boat.highlights.slice(0, 3).map((h, i) => <li key={i} className="flex gap-2"><span className="text-[#d9fb06] mt-1.5 w-1.5 h-1.5 rounded-full bg-[#d9fb06] flex-shrink-0" />{h}</li>)}
          </ul>
        )}
        <div className="flex flex-wrap gap-1.5 mt-auto">
          {(boat.programs || []).slice(0, 2).map((p) => <span key={p} className="text-xs px-2 py-0.5 rounded-full bg-[#1a1c1b] text-[#dfddd6] border border-[#3f4816]">{PROGRAM_LABELS[p]}</span>)}
          {boat.doc_level && <span className="text-xs px-2 py-0.5 rounded-full bg-[#1a1c1b] text-[#d9fb06] border border-[#3f4816]">{DOC_LEVEL_LABELS[boat.doc_level]}</span>}
        </div>
        <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#3f4816]">
          <span className="text-xs text-[#888680]">Mis à jour le {fmtDate(boat.updated_at)}</span>
          {!sold && <CompareButton boat={boat} />}
          <Link to={`/bateaux-a-vendre/${boat.slug}`} className="inline-flex items-center gap-1 text-sm font-bold text-[#d9fb06] hover:gap-2 transition-all" data-testid={`boat-open-${boat.slug}`}>
            Découvrir ce bateau <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </article>
  );
};

export default BoatCard;
